/**
 * Generates ABNT (NBR 6023) standard citation format.
 * @see https://connect.ebsco.com/s/article/Citing-Articles-in-ABNT-Style?language=en_US
 *
 * Structure: APELLIDO, Nombre. Título: subtítulo. Edición. Ciudad: Editorial, Año. p. XX.
 */

import {
    formatCitationWithText,
    getPageNumber,
    getPublicationPlace,
    getPublicationYear,
    getPublisher,
    getTitle,
    getValidContributors,
    hasCompleteAuthor,
    hasPublicationEdition,
    hasPublicationPlace,
    hasPublicationYear,
    hasTitle,
} from './citationHelpers.js';

/**
 * Formats a contributor name to ABNT standard: LASTNAME, Firstname.
 * @param {{first_name: string, last_name: string}} contributor - Contributor object
 * @returns {string} Formatted contributor name (e.g., "AUSTEN, Jane")
 */
function formatAbntContributorName(contributor) {
    const firstName = contributor.first_name?.trim() || '';
    const lastName = contributor.last_name?.trim() || '';

    if (!lastName || !firstName) {
        return '';
    }

    return `${lastName.toUpperCase()}, ${firstName}`;
}

/**
 * Formats the contributors list according to ABNT rules.
 * - 1 contributor: SILVA, João Carlos
 * - 2-3 contributors: Separated by semicolon
 * - 4+ contributors: First contributor + "et al."
 * @param {Object} tags - Issue tags containing contributors array
 * @returns {string} Formatted contributors string
 */
function getAbntContributors(tags) {
    const contributors = getValidContributors(tags);
    if (contributors.length === 0) {
        return '';
    }

    if (contributors.length === 1) {
        return formatAbntContributorName(contributors[0]);
    }

    if (contributors.length <= 3) {
        return contributors.map(formatAbntContributorName).join('; ');
    }

    return `${formatAbntContributorName(contributors[0])} et al`;
}

/**
 * Formats the title, optionally with subtitle.
 * Title is converted to sentence case according to ABNT NBR 6023.
 * @param {Object} issue - Issue object
 * @param {string} issueTitle - Fallback title
 * @param {Object} tags - Issue tags that may contain subtitle
 * @returns {string} Formatted title string
 */
function getAbntTitle(issue, issueTitle, tags) {
    const title = getTitle(issue, issueTitle);

    if (!title) {
        return '';
    }

    const sentenceCaseTitle = title.charAt(0).toUpperCase() + title.slice(1).toLowerCase();

    if (Object.prototype.hasOwnProperty.call(tags, 'subtitle') && tags.subtitle && tags.subtitle.length > 0) {
        const subtitle = Array.isArray(tags.subtitle) ? tags.subtitle[0] : tags.subtitle;
        if (subtitle && subtitle.trim()) {
            return `${sentenceCaseTitle}: ${subtitle.toLowerCase()}`;
        }
    }

    return sentenceCaseTitle;
}

/**
 * Formats the edition according to ABNT rules.
 * Only shown if not first edition, format: "2. ed."
 * @param {Object} tags - Issue tags containing edition
 * @returns {string} Formatted edition or empty string
 */
function getAbntEdition(tags) {
    if (!Object.prototype.hasOwnProperty.call(tags, 'edition') || !tags.edition || tags.edition.length === 0) {
        return '';
    }

    const edition = Array.isArray(tags.edition) ? tags.edition[0] : tags.edition;
    if (!edition) {
        return '';
    }

    const editionStr = String(edition).trim();
    const editionNumber = parseInt(editionStr, 10);

    if (editionNumber === 1 || editionStr === '' || editionStr.toLowerCase() === 'first') {
        return '';
    }

    if (!isNaN(editionNumber)) {
        return `${editionNumber}. ed`;
    }

    return editionStr;
}

/**
 * Formats the page number for ABNT citations.
 * @param {number|string|Object} currentLocation - Current page/location
 * @param {boolean} isEpub - Whether this is an EPUB file
 * @param {boolean} includePageNumber - Whether to include page number
 * @returns {string} Formatted page string or empty string
 */
function getAbntPage(currentLocation, isEpub, includePageNumber) {
    const pageNumber = getPageNumber(currentLocation, isEpub, includePageNumber);
    if (pageNumber !== null) {
        return `p. ${pageNumber}`;
    }
    return '';
}

/**
 * Generates the full ABNT citation string.
 * @param {string} text - Selected text to cite
 * @param {Object} issue - Issue/publication metadata
 * @param {number|string|Object} currentLocation - Current page/location
 * @param {string} issueTitle - Publication title
 * @param {boolean} isEpub - Whether this is an EPUB file
 * @returns {string} Formatted ABNT citation
 */
const AbntCitation = (text, issue, currentLocation, issueTitle, isEpub) => {
    const tags = issue.tags || {};
    const includePageNumber = text && text.trim().length > 0;

    const authors = getAbntContributors(tags);
    const title = getAbntTitle(issue, issueTitle, tags);
    const edition = getAbntEdition(tags);
    const place = getPublicationPlace(issue);
    const publisher = getPublisher(tags);
    const year = getPublicationYear(issue);
    const page = getAbntPage(currentLocation, isEpub, includePageNumber);

    const parts = [];

    if (authors) {
        parts.push(`${authors}.`);
    }

    if (title) {
        parts.push(`${title}.`);
    }

    if (edition) {
        parts.push(`${edition}.`);
    }

    if (place && publisher) {
        parts.push(`${place}: ${publisher},`);
    } else if (publisher) {
        parts.push(`${publisher},`);
    } else if (place) {
        parts.push(`${place}:`);
    }

    if (year) {
        parts.push(`${year}.`);
    }

    if (page) {
        parts.push(`${page}.`);
    }

    const citation = parts.join(' ');

    return formatCitationWithText(text, citation);
};

/**
 * Validates that the required metadata for ABNT citation format is available.
 * @param {Object} issue - Issue object with tags and publication_date
 * @param {string} title - Title from store
 * @returns {boolean} True if ABNT requirements are met
 */
export function validate(issue, title) {
    return (
        hasCompleteAuthor(issue?.tags) &&
        hasTitle(title) &&
        hasPublicationPlace(issue) &&
        hasPublicationEdition(issue?.tags) &&
        hasPublicationYear(issue)
    );
}

export default AbntCitation;
